import { useCallback, useEffect, useRef } from 'react';
import { bridge } from 'shared/src/utils';
import { useRetailAd } from '../queries/useRetailAd';
import type { RetailAdInfo } from 'shared/src/api';

const getAdInfoParams = (adInfo: RetailAdInfo) => ({
  AD_ID: adInfo.ad_executable_id,
  AD_PLACEMENT_ID: adInfo.ad_placement_id,
  AD_NAME: adInfo.ad_executable_name,
  ADVERTISING_SUPPLIER: adInfo.ad_supplier,
  CREATIVE_TYPE: adInfo.creative_type,
  LINK: adInfo.creative_link,
});

export const useLogRetailAd = (adSlotId: number) => {
  const { data: retailAd, status } = useRetailAd(adSlotId);
  const isCallLogged = useRef(false);
  const isLoadLogged = useRef(false);
  const isDisplayLogged = useRef(false);

  useEffect(() => {
    if (status === 'pending' && !isCallLogged.current) {
      bridge.logAnalyticsEvent({
        name: 'CALL_ADS',
        channel: ['ablylog'],
        analytics: { SCREEN: 'OVERVIEW', AD_SLOT_ID: adSlotId },
      });
      isCallLogged.current = true;
    }
  }, [status, adSlotId]);

  useEffect(() => {
    if (status === 'success' && !isLoadLogged.current) {
      bridge.logAnalyticsEvent({
        name: 'LOAD_ADS',
        channel: ['ablylog'],
        analytics: {
          SCREEN: 'OVERVIEW',
          AD_SLOT_ID: adSlotId,
          STATUS: retailAd?.ad_response_type,
          ...((retailAd?.ad_info != null) ? getAdInfoParams(retailAd.ad_info) : {}),
        },
      });
      isLoadLogged.current = true;
    }
  }, [status, retailAd, adSlotId]);

  const logDisplayAds = useCallback(() => {
    if (isDisplayLogged.current) return;
    if (retailAd?.ad_info == null) return;

    bridge.logAnalyticsEvent({
      name: 'DISPLAY_ADS',
      channel: ['ablylog'],
      analytics: {
        SCREEN: 'OVERVIEW',
        ...getAdInfoParams(retailAd.ad_info),
      },
    });
    isDisplayLogged.current = true;
  }, [retailAd]);

  const logClickAds = useCallback(() => {
    if (retailAd?.ad_info == null) return;

    bridge.logAnalyticsEvent({
      name: 'CLICK_ADS',
      channel: ['ablylog'],
      analytics: {
        SCREEN: 'OVERVIEW',
        ...getAdInfoParams(retailAd.ad_info),
      },
    });
  }, [retailAd]);

  return { logDisplayAds, logClickAds };
};
